import { SanityClient } from '@sanity/client'
import {
  errorPageQuery,
  homePageQuery,
  pageQuery,
  sitemapPagesQuery,
  allSlugsQuery,
  sitemapStaticPagesQuery,
  casePageQuery,
} from '@data/sanity/queries/page'
import {
  SanityErrorPageQuery,
  SanityHomePageQuery,
  SanityPageQuery,
  SanityAllSlugs,
  SanitySitemapStaticPagesQuery,
  SanitySitemapPagesQuery,
  SanityCasePageQuery,
} from '@data/sanity/queries/types/page'
import {
  SanityBlogAuthorSlugQueryParameter,
  SanityBlogCategorySlugQueryParameter,
  SanityBlogLocaleQueryParameter,
  SanityBlogPostSlugQueryParameter,
  SanityCaseSlugQueryParameter,
  SanityDocumentType,
  SanityLocaleQueryParameter,
  SanityLocalesQueryParameter,
  SanityPageSlugsQueryParameter,
  SanityTypeQueryParameter,
  SanityTypesQueryParameter,
} from '@data/sanity/queries/types/parameters'
import { Locale } from '@lib/language'
import { PageType } from '@lib/routes'
import { fetchQuery } from './client'

export type SanityModuleQueryParameters = SanityBlogAuthorSlugQueryParameter &
  SanityBlogCategorySlugQueryParameter &
  SanityBlogPostSlugQueryParameter &
  SanityCaseSlugQueryParameter &
  SanityBlogLocaleQueryParameter

export const defaultModuleQueryParameters: SanityModuleQueryParameters = {
  blogAuthorSlug: null,
  blogCategorySlug: null,
  blogPostSlug: null,
  caseSlug: null,
  blogLocale: null,
}

/**
 * Fetches all document slugs in all locales.
 */
export const getAllSlugs = async (
  sanityClient: SanityClient,
  type: SanityDocumentType,
) =>
  fetchQuery<SanityAllSlugs, SanityTypeQueryParameter>(
    sanityClient,
    allSlugsQuery,
    {
      ...defaultModuleQueryParameters,
      type,
    },
  )

/**
 * Fetches pages for sitemap.
 */
export const getSitemapPages = async (
  sanityClient: SanityClient,
  locales: Locale[],
  type: SanityDocumentType,
) =>
  fetchQuery<
    SanitySitemapPagesQuery,
    SanityTypeQueryParameter & SanityLocalesQueryParameter
  >(sanityClient, sitemapPagesQuery, {
    ...defaultModuleQueryParameters,
    type,
    locales,
  })

/**
 * Fetches static pages for sitemap.
 */
export const getSitemapStaticPages = async (
  sanityClient: SanityClient,
  locales: Locale[],
) =>
  fetchQuery<
    SanitySitemapStaticPagesQuery,
    SanityLocalesQueryParameter & SanityTypesQueryParameter
  >(sanityClient, sitemapStaticPagesQuery, {
    ...defaultModuleQueryParameters,
    locales,
    types: [PageType.HOME_PAGE, PageType.BLOG_PAGE],
  })

/**
 * Fetches the home page.
 */
export const getHomePage = async (sanityClient: SanityClient, locale: Locale) =>
  fetchQuery<SanityHomePageQuery, SanityLocaleQueryParameter>(
    sanityClient,
    homePageQuery,
    {
      ...defaultModuleQueryParameters,
      locale,
    },
  )

/**
 * Fetches the error page.
 */
export const getErrorPage = async (
  sanityClient: SanityClient,
  locale: Locale,
) =>
  fetchQuery<SanityErrorPageQuery, SanityLocaleQueryParameter>(
    sanityClient,
    errorPageQuery,
    {
      ...defaultModuleQueryParameters,
      locale,
    },
  )

/**
 * Fetches a specific page based on slug.
 */
export const getPage = async (
  sanityClient: SanityClient,
  locale: Locale,
  slug: string,
) =>
  fetchQuery<
    SanityPageQuery,
    SanityPageSlugsQueryParameter & SanityLocaleQueryParameter
  >(sanityClient, pageQuery, {
    ...defaultModuleQueryParameters,
    pageSlugs: [`/${slug}`, slug, `/${slug}/`],
    locale,
  })

/**
 * Fetches a case page.
 */
export const getCasePage = async (
  sanityClient: SanityClient,
  locale: Locale,
  slug: string,
) =>
  fetchQuery<
    SanityCasePageQuery,
    SanityCaseSlugQueryParameter & SanityLocaleQueryParameter
  >(sanityClient, casePageQuery, {
    ...defaultModuleQueryParameters,
    caseSlug: slug,
    locale,
  })
